import { loadScript } from 'dashboard/helper/DOMHelpers';

// Requesting this feature type is what makes Meta replace the WABA selection screen with the
// "connect your existing WhatsApp Business account" screen. Without it the flow silently falls
// back to Cloud-API-only onboarding and coexistence becomes unreachable. It has already been
// lost twice to upstream merges (#83 and the v4.16.2 merge) — keep it wired to a test.
export const COEXISTENCE_FEATURE_TYPE = 'whatsapp_business_app_onboarding';

export const loadFacebookSdk = async () => {
  return loadScript('https://connect.facebook.net/en_US/sdk.js', {
    async: true,
    defer: true,
    crossOrigin: 'anonymous',
  });
};

export const initializeFacebook = (appId, apiVersion) => {
  const version = apiVersion || 'v25.0';
  return new Promise(resolve => {
    const init = () => {
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version,
      });
      resolve();
    };

    if (window.FB) {
      init();
    } else {
      window.fbAsyncInit = init;
    }
  });
};

// Coexistence completions carry only `waba_id` — Meta omits `business_id` and
// `phone_number_id` from the FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING payload. The backend
// recovers the phone number from the WABA, so `waba_id` alone is enough for that flow.
export const isValidBusinessData = (
  businessData,
  { requireBusinessId = true } = {}
) => {
  if (!businessData || !businessData.waba_id) return false;

  return requireBusinessId ? Boolean(businessData.business_id) : true;
};

export const createMessageHandler = onEmbeddedSignupData => {
  return event => {
    if (!event.origin.endsWith('facebook.com')) return;

    try {
      let data;
      if (typeof event.data === 'string') {
        data = JSON.parse(event.data);
      } else if (typeof event.data === 'object' && event.data !== null) {
        data = event.data;
      } else {
        return;
      }

      if (data.type === 'WA_EMBEDDED_SIGNUP') {
        onEmbeddedSignupData(data);
      }
    } catch {
      // Ignore non-JSON or irrelevant messages
    }
  };
};

export const initWhatsAppEmbeddedSignup = (
  configId,
  featureType = 'profile_management_whatsapp'
) => {
  return new Promise((resolve, reject) => {
    window.FB.login(
      response => {
        if (response.authResponse && response.authResponse.code) {
          resolve(response.authResponse.code);
        } else if (response.error) {
          reject(new Error(response.error));
        } else {
          reject(new Error('Login cancelled'));
        }
      },
      {
        config_id: configId,
        scope:
          'whatsapp_business_management,whatsapp_business_messaging,whatsapp_business_manage_events',
        response_type: 'code',
        override_default_response_type: true,
        // `sessionInfoVersion` is only required on v2 — v3 and up enable session logging
        // automatically. `featureType` stays: Meta's versions doc claims extras is empty on v4,
        // but the v4 and coexistence pages (both newer) confirm WhatsApp Business app onboarding
        // is still selected through it.
        extras: {
          setup: {},
          featureType,
        },
      }
    );
  });
};

// Meta Pixel PageView for embedded signup conversion tracking — Meta correlates these
// PageViews (via the `feature` param) with signup completions to report conversion rates.
export const trackEmbeddedSignupPageView = () => {
  const pixelId = window.chatwootConfig?.whatsappPixelId;
  const appId = window.chatwootConfig?.whatsappAppId;
  if (!pixelId || !appId) return;

  if (!window.fbq) {
    const fbq = (...args) => {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    // eslint-disable-next-line no-underscore-dangle -- `_fbq` is the global name Meta's snippet defines
    window._fbq = window._fbq || fbq;
    loadScript('https://connect.facebook.net/en_US/fbevents.js', {
      async: true,
    }).catch(() => {
      // Pixel is best-effort analytics — never block the signup flow
    });
  }

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView', {
    appId,
    feature: 'whatsapp_embedded_signup',
  });
};

export const setupFacebookSdk = async (appId, apiVersion) => {
  const version = apiVersion || 'v25.0';
  await loadFacebookSdk();
  await initializeFacebook(appId, version);
};
